import {
  createContext,
  type CSSProperties,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { klasse } from '../ui/klasse.js';
import { LinienSymbol } from '../ui/linien-symbol.js';
import { istErlaubteBildquelle } from './als-html.js';
import stile from './bild-lupe.module.css';

/**
 * Drive-Vorschau in der Fassung fuers Vollbild. Gemessen am 2026-09-30: `sz=w2000` liefert
 * 2000 x 1116 (3,1 MB), das Original waere 5056 x 3392 (21 MB). Alle anderen Quellen bleiben
 * unveraendert -- ob sie gezeigt werden, entscheidet danach die Allowlist.
 */
export function grosseFassung(url: string): string {
  let adresse: URL;
  try {
    adresse = new URL(url);
  } catch {
    return url;
  }
  if (
    adresse.protocol !== 'https:' ||
    adresse.hostname !== 'drive.google.com' ||
    adresse.pathname !== '/thumbnail'
  ) {
    return url;
  }
  adresse.searchParams.set('sz', 'w2000');
  return adresse.toString();
}

export interface LupenBild {
  vorschau: string;
  name: string;
  /** Kopfzeile des Dialogs wie in der Content-Factory ("Beitragsbild 1 von 1"); sonst der Name. */
  titel?: string;
  /**
   * Die Seite zeigt genau diese Adresse schon selbst als `<img>` (Ergebnisseite: die fertigen
   * Bilder liegen auch beim Bildgenerator). Dann schuetzt die Allowlist hier vor nichts mehr --
   * der Browser hat die Adresse ohnehin geladen --, verlangt wird nur noch https.
   */
  schonGezeigt?: boolean;
}

export function istHttps(wert: string): boolean {
  try {
    return new URL(wert).protocol === 'https:';
  } catch {
    return false;
  }
}

const LupenKontext = createContext<{ oeffnen(bild: LupenBild): void } | null>(null);

/** Seitenverhaeltnis, bis die kleine Fassung ihr eigenes meldet (Drive-Vorschauen: ~1,79). */
const VERHAELTNIS_VORGABE = 16 / 9;

/**
 * Genau ein Vollbild-Dialog pro Seite (Spec §3). Im DOM haengt er per Portal an `body`, weil
 * Hero und Galerie in der Textflaeche liegen (DOM-Vertrag). Im React-Baum steht er NEBEN den
 * Kindern: Synthetische Ereignisse aus dem Dialog laufen durch den React-Baum, nicht durch das
 * DOM, und erreichten sonst Figur und Textflaeche.
 */
export function BildLupeProvider({ children }: { children: ReactNode }) {
  const [offen, setOffen] = useState<LupenBild | null>(null);
  const [scharf, setScharf] = useState(false);
  const [verhaeltnis, setVerhaeltnis] = useState(VERHAELTNIS_VORGABE);
  const dialog = useRef<HTMLDialogElement>(null);
  const ausloeser = useRef<HTMLElement | null>(null);
  // Nur ein Klick, der auf dem Hintergrund BEGINNT, schliesst (Review Focus 1): Wer im Bild
  // die Maus drueckt und daneben loslaesst, bekommt sonst einen `click` auf dem Dialog.
  const druckAufHintergrund = useRef(false);
  const titelId = useId();

  const oeffnen = useCallback((bild: LupenBild) => {
    // Beide Adressen durch die Allowlist: sie ist hier die einzige Verteidigung (als-html.ts).
    const zeigbar = (adresse: string) =>
      istErlaubteBildquelle(adresse) || (bild.schonGezeigt === true && istHttps(adresse));
    if (!zeigbar(bild.vorschau) || !zeigbar(grosseFassung(bild.vorschau))) return;
    ausloeser.current =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;
    setScharf(false);
    setVerhaeltnis(VERHAELTNIS_VORGABE);
    setOffen(bild);
  }, []);

  useEffect(() => {
    const element = dialog.current;
    if (offen && element && !element.open) element.showModal();
  }, [offen]);

  function geschlossen() {
    setOffen(null);
    const ziel = ausloeser.current;
    ausloeser.current = null;
    // Ausdruecklich statt ueber die Fokus-Rueckgabe des Browsers (Spec §4.2).
    if (ziel?.isConnected) ziel.focus();
  }

  const wert = useMemo(() => ({ oeffnen }), [oeffnen]);
  const gross = offen ? grosseFassung(offen.vorschau) : null;
  // Ohne eigene grosse Fassung (data:, lh3) traegt die kleine den Namen.
  const nurEins = offen !== null && gross === offen.vorschau;

  return (
    <LupenKontext.Provider value={wert}>
      {children}
      {offen &&
        gross &&
        createPortal(
          <dialog
            ref={dialog}
            className={stile.dialog}
            data-bild-lupe=""
            aria-labelledby={titelId}
            onClose={geschlossen}
            onMouseDown={(e) => {
              druckAufHintergrund.current = e.target === e.currentTarget;
            }}
            onClick={(e) => {
              // Die Blende und der weisse Rand der Karte sind beide das dialog-Element selbst.
              // Geschlossen wird nur ausserhalb der Karte, wie bei der Blende der Content-Factory.
              const rahmen = e.currentTarget.getBoundingClientRect();
              const aufKarte =
                e.clientX > rahmen.left &&
                e.clientX < rahmen.right &&
                e.clientY > rahmen.top &&
                e.clientY < rahmen.bottom;
              if (e.target === e.currentTarget && druckAufHintergrund.current && !aufKarte) {
                e.currentTarget.close();
              }
              druckAufHintergrund.current = false;
            }}
          >
            {/* Kopf wie der Vergroessern-Dialog der Content-Factory (Rueckmeldung 2026-10-13):
                Titel links, Schliessen-Kreuz rechts, darunter das Bild ungeschnitten. */}
            <div className={stile.kopf}>
              <h2 id={titelId} className={stile.titel} title={offen.titel ?? offen.name}>
                {offen.titel ?? offen.name}
              </h2>
              <button
                type="button"
                className={stile.schliessen}
                aria-label="Schließen"
                onClick={() => dialog.current?.close()}
              >
                <LinienSymbol name="x" groesse={18} />
              </button>
            </div>
            <div
              className={stile.bildfeld}
              style={{ '--seitenverhaeltnis': String(verhaeltnis) } as CSSProperties}
            >
              <img
                className={stile.bild}
                src={offen.vorschau}
                alt={nurEins ? offen.name : ''}
                aria-hidden={nurEins ? undefined : 'true'}
                onLoad={(e) => {
                  const { naturalWidth: breite, naturalHeight: hoehe } = e.currentTarget;
                  if (breite > 0 && hoehe > 0) setVerhaeltnis(breite / hoehe);
                }}
              />
              {!nurEins && (
                <img
                  className={klasse(stile.bild, stile.gross)}
                  src={gross}
                  alt={offen.name}
                  data-scharf={scharf ? '' : undefined}
                  onLoad={() => setScharf(true)}
                  onError={() => setScharf(false)}
                />
              )}
            </div>
          </dialog>,
          document.body,
        )}
    </LupenKontext.Provider>
  );
}

export function useBildLupe(): { oeffnen(bild: LupenBild): void } {
  const kontext = useContext(LupenKontext);
  if (!kontext) throw new Error('useBildLupe braucht einen BildLupeProvider');
  return kontext;
}

/**
 * Nur-Symbol-Knopf; die Bedeutung traegt das `aria-label` (siehe `LinienSymbol`). Runder weisser
 * Knopf mit dem Vergroessern-Zeichen wie am Beitragsbild der Content-Factory; wo er am Bild sitzt,
 * legt der Aufrufer ueber `className` fest.
 */
export function LupenKnopf({
  name,
  vorschau,
  titel,
  schonGezeigt,
  className,
}: {
  name: string;
  vorschau: string;
  titel?: string | undefined;
  schonGezeigt?: boolean | undefined;
  // `| undefined` wegen `exactOptionalPropertyTypes`: CSS-Modul-Klassen sind `string | undefined`.
  className?: string | undefined;
}) {
  const { oeffnen } = useBildLupe();
  return (
    <button
      type="button"
      className={klasse(stile.knopf, className)}
      aria-label={`${name} vergrößern`}
      onClick={() =>
        oeffnen({
          vorschau,
          name,
          ...(titel === undefined ? {} : { titel }),
          ...(schonGezeigt === undefined ? {} : { schonGezeigt }),
        })
      }
    >
      <LinienSymbol name="maximize" groesse={16} />
    </button>
  );
}
